import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Boxes } from 'lucide-react'
import { FilterChip, SectionHeader } from '@loop3/ui'
import { EntityGrid } from '@/components/entity-card'
import { useSolutionsLibrary } from '@/hooks/useSolutionsLibrary'
import {
  solutionsLibraryHref,
  stageCoverage,
} from '@/lib/compute/solutions-library'

const MAX_CARDS = 6

/** A client's solutions with knowledge-loop stage coverage; renders nothing when there are none. */
export function ClientSolutions({ prefix }: { prefix: string }) {
  const library = useSolutionsLibrary()
  const [stage, setStage] = useState<string | null>(null)
  const rows = (library.data ?? []).filter((s) => s.client?.prefix === prefix)
  if (rows.length === 0) return null

  const shown = stage ? rows.filter((s) => s.stages.includes(stage)) : rows
  const href = solutionsLibraryHref({ client: prefix, stage })

  return (
    <section>
      <SectionHeader icon={Boxes} label="What we've built" meta={rows.length} />
      <div className="mb-4 flex flex-wrap gap-1.5">
        {stageCoverage(rows).map(({ stage: s, count }) => (
          <FilterChip
            key={s}
            active={stage === s}
            disabled={count === 0 && stage !== s}
            title={
              count === 0 ? 'Gap: nothing built for this stage yet' : undefined
            }
            onClick={() => setStage(stage === s ? null : s)}
          >
            {s} · {count}
          </FilterChip>
        ))}
      </div>
      <EntityGrid
        items={shown.slice(0, MAX_CARDS).map((s) => ({
          kind: 'solution',
          id: s.id,
          title: s.name,
          subtitle: s.lifecycle ?? undefined,
        }))}
      />
      <Link
        to={href}
        className="mt-3 inline-block text-sm text-[var(--l3-violet)] hover:underline"
      >
        Show all {shown.length} →
      </Link>
    </section>
  )
}
